/**
 * FleetTracko Admin Dashboard Types
 */

// Re-export shared types
export type {
  ApiResponse,
  ApiError,
  PaginationMeta,
  PaginationParams,
  User as SharedUser,
  Role,
  AuthTokens,
  LoginCredentials,
  AuthUser,
  Tenant,
  TenantSettings,
  Customer,
  Vehicle,
  VehicleStatus,
  VehicleLocation,
  Yard,
  YardZone,
  YardZoneType,
  Address,
  Coordinates,
  WorkflowDefinition,
  WorkflowStep,
  WorkflowTransition,
  WorkflowInstance,
  WorkflowStatus,
  WorkflowHistoryEntry,
} from '@fleettracko/shared';

export type {
  UserRole,
  JwtPayload,
  Permission,
  PermissionResource,
  PermissionAction,
  TenantContext,
} from '@fleettracko/shared';

import type { UserRole } from '@fleettracko/shared';

// German labels for role display
export const ROLE_LABELS: Record<UserRole, string> = {
  SUPER_ADMIN: 'Super-Admin',
  ADMIN: 'Administrator',
  DISPATCHER: 'Disponent',
  YARD_WORKER: 'Hofarbeiter',
  WORKSHOP: 'Werkstatt',
  CLEANING: 'Reinigung',
  QC_INSPECTOR: 'QS-Prüfer',
  CUSTOMER_ADMIN: 'Kunden-Admin',
  CUSTOMER_USER: 'Kundenportal',
  CUSTOMER_READONLY: 'Kunden (Lesezugriff)',
  INSURANCE_USER: 'Versicherung',
  APPRAISER: 'Gutachter',
  SUPPLIER: 'Lieferant',
};

// Extended User type that includes auth properties returned by the API
export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  tenantId: string;
  customerId?: string;
  role?: string;
  roles?: string[];
  additionalRoles?: string[];
  isActive: boolean;
  lastLoginAt?: string;
  createdAt: string;
  updatedAt: string;
}

// Admin-specific types

export interface DashboardStats {
  incoming: number;
  inProgress: number;
  ready: number;
  overdue: number;
  todayArrivals: number;
  todayDepartures: number;
}

export interface ZoneOccupancy {
  zoneId: string;
  zoneName: string;
  zoneCode: string;
  capacity: number;
  occupied: number;
  utilization: number;
}

export interface UrgentItem {
  id: string;
  vehicleId: string;
  vin: string;
  licensePlate?: string;
  task: string;
  slaDeadline: string;
  slaStatus: 'on_track' | 'warning' | 'overdue';
  minutesRemaining: number;
  assignedTo?: string;
  customer: string;
}

export interface Task {
  id: string;
  tenantId: string;
  vehicleId: string;
  workflowInstanceId?: string;
  type: TaskType;
  title: string;
  description?: string;
  status: TaskStatus;
  priority: TaskPriority;
  assignedTo?: string;
  assignedToUser?: {
    id: string;
    firstName: string;
    lastName: string;
  };
  // Expanded relations (populated when API returns with relations)
  vehicle?: {
    id: string;
    vin: string;
    licensePlate?: string;
    make?: string;
    model?: string;
  };
  assignee?: {
    id: string;
    firstName: string;
    lastName: string;
    role?: string;
  };
  dueDate?: string;
  completedAt?: string;
  completedBy?: string;
  notes?: string;
  metadata?: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export type TaskType =
  | 'CHECKIN'
  | 'DAMAGE_ASSESSMENT'
  | 'KVA_CREATION'
  | 'REPAIR'
  | 'CLEANING'
  | 'QC_INSPECTION'
  | 'FINAL_CHECK'
  | 'HANDOVER'
  | 'MOVE'
  | 'CUSTOM';

export type TaskStatus =
  | 'PENDING'
  | 'ASSIGNED'
  | 'IN_PROGRESS'
  | 'ON_HOLD'
  | 'COMPLETED'
  | 'CANCELLED'
  | 'FAILED';

export type TaskPriority = 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT';

export interface YardPosition {
  id: string;
  zoneId: string;
  code: string;
  row: number;
  column: number;
  vehicleId?: string;
  vehicle?: {
    id: string;
    vin: string;
    licensePlate?: string;
    make?: string;
    model?: string;
    color?: string;
    status: string;
  };
  isBlocked: boolean;
  isOccupied?: boolean;
  zoneName?: string;
  zoneCode?: string;
  locationName?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Contract {
  id: string;
  customerId: string;
  name: string;
  startDate: string;
  endDate?: string;
  isActive: boolean;
  terms: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface PriceListItem {
  id: string;
  customerId: string;
  serviceCode: string;
  serviceName: string;
  unitPrice: number;
  currency: string;
  unit: string;
  validFrom: string;
  validTo?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Report {
  id: string;
  name: string;
  type: ReportType;
  parameters: Record<string, unknown>;
  generatedAt: string;
  generatedBy: string;
  format: 'pdf' | 'excel' | 'csv';
  downloadUrl?: string;
}

export type ReportType = 'sla' | 'throughput' | 'capacity' | 'billing' | 'custom';

export interface SLAMetrics {
  totalVehicles: number;
  onTime: number;
  late: number;
  onTimePercentage: number;
  averageProcessingTime: number;
  byTask: {
    taskType: TaskType;
    onTime: number;
    late: number;
    averageTime: number;
  }[];
}

export interface ThroughputMetrics {
  period: string;
  arrivals: number;
  departures: number;
  processed: number;
  byDay: {
    date: string;
    arrivals: number;
    departures: number;
  }[];
}

export interface CapacityMetrics {
  totalCapacity: number;
  currentOccupancy: number;
  utilizationPercent: number;
  byZone: ZoneOccupancy[];
  trend: {
    date: string;
    occupancy: number;
  }[];
}

// Filter types
export interface VehicleFilters {
  status?: string; // Single status filter
  statuses?: string[]; // Multiple statuses filter
  customer?: string[];
  zone?: string[];
  make?: string;
  search?: string;
  dateFrom?: string;
  dateTo?: string;
}

export interface TaskFilters {
  status?: TaskStatus; // Single status filter
  statuses?: TaskStatus[]; // Multiple statuses filter
  taskType?: string; // Single task type filter
  taskTypes?: string[]; // Multiple task types filter
  priority?: TaskPriority; // Single priority filter
  assigneeId?: string;
  vehicleId?: string;
  workflowInstanceId?: string;
  customerId?: string;
  isAssigned?: boolean;
  isOverdue?: boolean;
  search?: string;
  dueFrom?: string;
  dueTo?: string;
  createdFrom?: string;
  createdTo?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface UserFilters {
  role?: string[];
  isActive?: boolean;
  search?: string;
}

// Invoice Types
export type InvoiceStatus =
  | 'DRAFT'
  | 'PENDING_APPROVAL'
  | 'APPROVED'
  | 'SENT'
  | 'PAID'
  | 'PARTIALLY_PAID'
  | 'OVERDUE'
  | 'CANCELLED'
  | 'DISPUTED'
  | 'CREDITED';

export type InvoiceType = 'INVOICE' | 'CREDIT_NOTE' | 'PROFORMA';

export interface InvoiceCustomer {
  id: string;
  code: string;
  name: string;
  contactEmail?: string;
  taxNumber?: string;
}

export interface InvoiceItem {
  id: string;
  serviceCode: string;
  description: string;
  quantity: string;
  unit: string;
  unitPrice: string;
  totalPrice: string;
  taxRate: string;
  taxAmount: string;
  serviceRecordId?: string;
  vehicleVin?: string;
  notes?: string;
  createdAt: string;
}

export interface Invoice {
  id: string;
  invoiceNumber: string;
  type: InvoiceType;
  status: InvoiceStatus;
  customerId: string;
  customer?: InvoiceCustomer;
  invoiceDate: string;
  dueDate: string;
  subtotal: string;
  taxAmount: string;
  totalAmount: string;
  amountPaid: string;
  outstandingAmount: string;
  currency: string;
  paymentTerms?: string;
  periodStart?: string;
  periodEnd?: string;
  notes?: string;
  internalNotes?: string;
  sentAt?: string;
  paidAt?: string;
  itemCount: number;
  items?: InvoiceItem[];
  isOverdue: boolean;
  daysOverdue?: number;
  allowedTransitions: InvoiceStatus[];
  createdAt: string;
  updatedAt: string;
}

export interface InvoiceFilters {
  status?: InvoiceStatus;
  customerId?: string;
  vehicleId?: string;
  search?: string;
  dateFrom?: string;
  dateTo?: string;
  isOverdue?: boolean;
}

export interface BillingSummary {
  totalInvoiced: string;
  totalPaid: string;
  totalOutstanding: string;
  totalOverdue: string;
  invoiceCount: number;
  overdueCount: number;
  avgDaysToPayment: number;
  byStatus?: Record<string, { count: number; amount: string }>;
}

// KVA Types
export type KVAStatus =
  | 'DRAFT'
  | 'PENDING_APPROVAL'
  | 'APPROVED'
  | 'PARTIALLY_APPROVED'
  | 'REJECTED'
  | 'EXPIRED';

export interface KVAItem {
  id: string;
  description: string;
  category: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  isApproved?: boolean;
  notes?: string;
  photos?: string[];
}

export interface KVA {
  id: string;
  kvaNumber: string;
  status: KVAStatus;
  damageCaseId: string;
  vehicleId: string;
  customerId?: string;
  customer?: { id: string; name: string; code: string };
  vehicle?: { id: string; vin: string; licensePlate?: string; make?: string; model?: string };
  items: KVAItem[];
  repairOrders?: { id: string; orderNumber: string; status: string }[];
  totalAmount: number;
  approvedAmount?: number;
  notes?: string;
  rejectionReason?: string;
  approvedAt?: string;
  approvedBy?: string;
  expiresAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface KVAFilters {
  status?: KVAStatus;
  customerId?: string;
  vehicleId?: string;
  search?: string;
}

// Damage Case Types - matches database enum
export type DamageCaseStatus =
  | 'OPEN'
  | 'IN_PROGRESS'
  | 'ASSESSED'
  | 'APPROVED'
  | 'REJECTED'
  | 'CLOSED'
  | 'CANCELLED';

export interface DamageItem {
  id: string;
  position: string;
  damageType: string;
  severity: string;
  description?: string;
  estimatedCost?: number;
  photos?: string[];
}

export interface DamageCase {
  id: string;
  caseNumber: string;
  status: DamageCaseStatus;
  vehicleId: string;
  vehicle?: { id: string; vin: string; licensePlate?: string; make?: string; model?: string };
  customerId?: string;
  customer?: { id: string; name: string; code: string };
  items: DamageItem[];
  totalEstimatedCost?: number;
  notes?: string;
  reportedBy?: string;
  kvaId?: string;
  kvas?: Array<{ id: string; kvaNumber: string; status: string; totalAmount: number }>;
  repairOrderId?: string;
  insuranceCase?: { id: string; caseNumber: string; status: string };
  createdAt: string;
  updatedAt: string;
}

export interface DamageCaseFilters {
  status?: DamageCaseStatus;
  customerId?: string;
  vehicleId?: string;
  search?: string;
}

// Repair Order Types
export type RepairOrderStatus =
  | 'CREATED'
  | 'PARTS_ORDERED'
  | 'PARTS_RECEIVED'
  | 'IN_PROGRESS'
  | 'QC_PENDING'
  | 'COMPLETED'
  | 'CANCELLED';

export interface RepairOrderItem {
  id: string;
  itemType: string;
  description: string;
  partNumber?: string;
  quantity: number;
  unit: string;
  unitPrice: number;
  totalPrice: number;
}

export interface RepairTimeLog {
  id: string;
  activity: string;
  startTime: string;
  endTime?: string;
  durationMinutes?: number;
  notes?: string;
  user?: { id: string; firstName: string; lastName: string };
  createdAt: string;
}

export interface RepairOrder {
  id: string;
  orderNumber: string;
  status: RepairOrderStatus;
  vehicleId: string;
  vehicle?: { id: string; vin: string; licensePlate?: string; make?: string; model?: string };
  kvaId?: string;
  kva?: {
    id: string;
    kvaNumber: string;
    status: string;
    totalAmount: number;
    approvedAmount?: number;
  };
  title?: string;
  description?: string;
  priority?: string;
  isInternal: boolean;
  supplierId?: string;
  estimatedHours?: number;
  actualHours?: number;
  estimatedCost?: number;
  actualCost?: number;
  scheduledStart?: string;
  scheduledEnd?: string;
  startedAt?: string;
  completedAt?: string;
  items?: RepairOrderItem[];
  timeLogs?: RepairTimeLog[];
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface RepairOrderFilters {
  status?: RepairOrderStatus;
  vehicleId?: string;
  isInternal?: boolean;
  search?: string;
}

// Insurance Case Types
export type InsuranceCaseType =
  | 'LIABILITY'
  | 'COMPREHENSIVE'
  | 'PARTIAL_COMP'
  | 'TRANSPORT'
  | 'COMPOUND_FAULT'
  | 'GLASS_DAMAGE'
  | 'WILDLIFE'
  | 'THEFT'
  | 'VANDALISM'
  | 'WEATHER'
  | 'OTHER';

export type InsuranceCaseStatus =
  | 'NEW'
  | 'REPORTED'
  | 'APPRAISER_SCHEDULED'
  | 'APPRAISER_COMPLETED'
  | 'APPROVAL_PENDING'
  | 'APPROVED'
  | 'IN_REPAIR'
  | 'TOTAL_LOSS'
  | 'COMPLETED'
  | 'CLOSED';

export interface InsuranceCaseDocument {
  id: string;
  category: string;
  name: string;
  notes?: string;
  documentId: string;
  document?: {
    id: string;
    name: string;
    filePath: string;
    mimeType: string;
    fileSize: number;
    originalFilename?: string;
  };
  uploadedById?: string;
  uploadedBy?: { id: string; firstName: string; lastName: string };
  createdAt: string;
  updatedAt: string;
}

export interface InsuranceCase {
  id: string;
  caseNumber: string;
  caseType: InsuranceCaseType;
  status: InsuranceCaseStatus;
  vehicleId: string;
  vehicle?: { id: string; vin: string; licensePlate?: string; make?: string; model?: string };
  customerId?: string;
  customer?: { id: string; name: string; code: string };
  damageCaseId?: string;
  damageCase?: { id: string; caseNumber: string; status: string };
  insurerName?: string;
  insuranceClaimNumber?: string;
  description?: string;
  incidentDate?: string;
  policeReportNumber?: string;
  estimatedAmount?: number;
  approvedAmount?: number;
  deductible?: number;
  claimedAmount?: number;
  invoicedAmount?: number;
  paidAmount?: number;
  recoveredAmount?: number;
  isTotalLoss: boolean;
  appraiserName?: string;
  appraiserScheduledAt?: string;
  appraiserCompletedAt?: string;
  reportedAt?: string;
  closedAt?: string;
  notes?: string;
  kvas?: Array<{
    id: string;
    kvaNumber: string;
    status: string;
    totalAmount: number;
    approvedAmount?: number;
  }>;
  documents?: InsuranceCaseDocument[];
  createdAt: string;
  updatedAt: string;
}

export interface InsuranceCaseFilters {
  status?: InsuranceCaseStatus;
  caseType?: InsuranceCaseType;
  customerId?: string;
  vehicleId?: string;
  search?: string;
}
